import { deserializeFromMarkdown } from '@/tiptap/markdown/deserializeFromMarkdown'
import type { UIMessage } from 'ai'
import type { CheckerSummary, PolicyToolProgress } from './types'

// ── Block-targeted apply helper ──

/**
 * Find the full section range starting from a block.
 * If the block is a heading, the section extends from the heading
 * to just before the next heading of same or higher level (or end of doc).
 * For non-heading blocks, returns just that block's range.
 */
export function getSectionRange(
  doc: any,
  targetPos: number,
  targetEnd: number,
  node: any
): { from: number; to: number } {
  if (node.type.name !== 'heading') {
    return { from: targetPos, to: targetEnd }
  }

  const headingLevel = node.attrs?.level ?? 1
  let sectionEnd = targetEnd

  let pastTarget = false
  doc.forEach((child: any, offset: number) => {
    if (pastTarget) return
    const childStart = offset
    const childEnd = offset + child.nodeSize
    if (childStart < targetEnd) return

    if (
      child.type.name === 'heading' &&
      (child.attrs?.level ?? 1) <= headingLevel
    ) {
      sectionEnd = childStart
      pastTarget = true
      return
    }
    sectionEnd = childEnd
  })

  return { from: targetPos, to: sectionEnd }
}

/**
 * Applies content to a specific block in the TipTap editor by block ID.
 * Supports replace, insert-after, and delete actions.
 */
/**
 * Normalize replacement markdown so it slots cleanly into the target block.
 * The LLM often copies the surrounding list numbering/bullet into the
 * replacement body (e.g. "3. Executive leadership..."). When the target
 * is a paragraph — especially one inside a list item — that leading
 * marker causes the markdown parser to produce a sibling orderedList /
 * bulletList that replaces not just the paragraph but the whole list.
 *
 * For paragraph/heading/blockquote targets, strip leading list markers
 * from every line so the content deserializes as plain prose.
 */
function normalizeReplacementMarkdown(
  content: string,
  targetType: string | undefined,
): string {
  if (
    targetType !== 'paragraph' &&
    targetType !== 'heading' &&
    targetType !== 'blockquote'
  ) {
    return content
  }
  return content
    .split('\n')
    .map((line) => line.replace(/^(\s*)(?:\d+\.|[-*+])\s+/, '$1'))
    .join('\n')
    .trim()
}

/**
 * If the deserialized markdown produced a single list wrapping a single
 * list item, unwrap it to the list item's inner content. This handles
 * the case where the LLM emitted a list with one item as the replacement
 * body for a plain paragraph.
 */
function unwrapSingleListItem(nodes: any): any {
  if (!Array.isArray(nodes) || nodes.length !== 1) return nodes
  const root = nodes[0]
  if (!root || (root.type !== 'orderedList' && root.type !== 'bulletList'))
    return nodes
  const items = Array.isArray(root.content) ? root.content : []
  if (items.length !== 1) return nodes
  const item = items[0]
  if (!item || item.type !== 'listItem') return nodes
  return Array.isArray(item.content) ? item.content : nodes
}

export function applyBlockToEditor(
  editor: any,
  blockId: string,
  action: string,
  content?: string,
  _label?: string,
): { ok: boolean } {
  const doc = editor.state.doc
  let targetPos: number | null = null
  let targetEnd: number | null = null
  let targetNode: any = null

  doc.descendants((node: any, pos: number) => {
    if (targetPos !== null) return false
    if (node.attrs?.id === blockId) {
      targetPos = pos
      targetEnd = pos + node.nodeSize
      targetNode = node
      return false
    }
  })

  if (targetPos === null || targetEnd === null || !targetNode) {
    console.error('[ApplyBlock] target block not found in doc', { blockId })
    return { ok: false }
  }

  console.log('[ApplyBlock] applyBlockToEditor', {
    blockId,
    action,
    found: targetNode != null,
    targetType: targetNode?.type?.name ?? null,
    targetPos,
    targetEnd,
    contentLength: content?.length ?? 0,
  })

  // For insert-after targeting a HEADING, insert after the heading's whole
  // section (its paragraphs), not immediately after the heading line — the
  // agent means "add a new section after this section". For a paragraph
  // target, insert-after stays right after that block.
  const isHeadingTarget = targetNode.type?.name === 'heading'
  const useSectionRange =
    action === 'replace' ||
    action === 'delete' ||
    (action === 'insert-after' && isHeadingTarget)
  const range = useSectionRange
    ? getSectionRange(doc, targetPos, targetEnd, targetNode)
    : { from: targetPos, to: targetEnd }

  console.log('[ApplyBlock] computed range', { action, range })

  if (action === 'delete') {
    const ok = editor
      .chain()
      .focus()
      .deleteRange({ from: range.from, to: range.to })
      .run()
    console.log('[ApplyBlock] delete ran', { ok })
    return { ok }
  }

  if (action === 'replace' && content) {
    const targetType = targetNode.type?.name
    const normalized = normalizeReplacementMarkdown(content, targetType)
    let ok = false
    try {
      const { content: parsed } = deserializeFromMarkdown(normalized)
      let parsedNodes: any = Array.isArray(parsed)
        ? parsed
        : (parsed?.content ?? [parsed])
      // Paragraph/heading/blockquote targets can't host lists — unwrap a
      // single-item list back to its inner nodes.
      if (
        targetType === 'paragraph' ||
        targetType === 'heading' ||
        targetType === 'blockquote'
      ) {
        parsedNodes = unwrapSingleListItem(parsedNodes)
      }
      console.log('[ApplyBlock] deserialized markdown', {
        targetType,
        normalizedLeadingChars: normalized.slice(0, 60),
        nodeCount: Array.isArray(parsedNodes) ? parsedNodes.length : 0,
        firstNodeType: parsedNodes?.[0]?.type ?? null,
      })

      // In-place inline replace when possible: keep the target block
      // node (and its id) intact; swap only its inline children. This
      // preserves the blockId so subsequent references (re-apply,
      // claim pill anchoring) still resolve.
      const canInlineReplace =
        (targetType === 'paragraph' || targetType === 'heading') &&
        Array.isArray(parsedNodes) &&
        parsedNodes.length === 1 &&
        parsedNodes[0]?.type === targetType &&
        Array.isArray(parsedNodes[0]?.content)

      if (canInlineReplace) {
        const innerContent = parsedNodes[0].content
        // Open boundaries: targetPos+1 .. targetEnd-1 addresses the
        // block's inline content without touching the block itself.
        ok = editor
          .chain()
          .focus()
          .insertContentAt(
            { from: targetPos + 1, to: targetEnd - 1 },
            innerContent,
          )
          .run()
        console.log('[ApplyBlock] replace (inline, id preserved) ran', {
          ok,
          preservedBlockId: targetNode.attrs?.id,
        })
      } else {
        ok = editor
          .chain()
          .focus()
          .insertContentAt({ from: range.from, to: range.to }, parsedNodes)
          .run()
        console.log('[ApplyBlock] replace (block-swap) ran', { ok })
      }
    } catch (err) {
      console.warn(
        '[ApplyBlock] markdown deserialize failed, falling back to raw string',
        err,
      )
      ok = editor
        .chain()
        .focus()
        .insertContentAt({ from: range.from, to: range.to }, normalized)
        .run()
      console.log('[ApplyBlock] replace (raw) ran', { ok })
    }
    return { ok }
  }

  if (action === 'insert-after' && content) {
    const insertAt = range.to
    let ok = false
    try {
      const { content: parsed } = deserializeFromMarkdown(content)
      const nodes = Array.isArray(parsed)
        ? parsed
        : (parsed?.content ?? [parsed])
      ok = editor.chain().focus().insertContentAt(insertAt, nodes).run()
      console.log('[ApplyBlock] insert-after (parsed) ran', { ok, insertAt })
    } catch (err) {
      console.warn(
        '[ApplyBlock] markdown deserialize failed, falling back to raw string',
        err,
      )
      ok = editor.chain().focus().insertContentAt(insertAt, content).run()
      console.log('[ApplyBlock] insert-after (raw) ran', { ok, insertAt })
    }
    return { ok }
  }

  console.warn('[ApplyBlock] no branch matched', { action, hasContent: !!content })
  return { ok: false }
}

// ── Tool progress parsing for generate-policies ──

export function parseToolPayload(payload: unknown) {
  if (!payload) return null
  if (typeof payload === 'object') return payload as Record<string, any>
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload) as Record<string, any>
    } catch {
      return null
    }
  }
  return null
}

export function isActivePolicyToolState(state: unknown): boolean {
  return (
    state === 'call' ||
    state === 'input-streaming' ||
    state === 'input-available' ||
    state === 'partial-call'
  )
}

export function extractPolicyToolProgress(
  messages: UIMessage[]
): PolicyToolProgress {
  let generateCount = 0
  let updateSectionCount = 0
  let recordClaimsCount = 0
  let checkerCount = 0
  let triageCount = 0
  let isGeneratingPolicy = false
  let latestPolicyDocId: string | null = null
  let latestDraftContent: string | null = null
  let latestDraftContentMode: 'replace' | 'append' | null = null
  let latestCheckerSummary: CheckerSummary | null = null
  let latestReasoning: string | null = null
  let requiresRegenerateConfirmation = false
  let seq = 0
  let lastCheckerSeq = -1
  let lastSectionUpdateSeq = -1

  for (const msg of messages) {
    if (msg.role !== 'assistant') continue
    for (const part of (msg.parts ?? []) as any[]) {
      // Capture reasoning/thinking text that appears before tool calls
      if (
        part.type === 'reasoning' &&
        typeof part.text === 'string' &&
        part.text.trim()
      ) {
        latestReasoning = part.text
        continue
      }

      if (part.type === 'tool-generatePolicy') {
        // v6 format: check part.state and part.output
        const partState = (part as any).state
        const partOutput = (part as any).output
        const isPrelim = (part as any).preliminary === true

        // Parse output (could be string or object)
        const result = partOutput ? parseToolPayload(partOutput) : null
        const hasDraftChunk =
          typeof result?.policyContent === 'string' &&
          result.policyContent.trim().length > 0
        const hasDraftDelta =
          typeof result?.policyContentDelta === 'string' &&
          result.policyContentDelta.length > 0
        const isInterimOutput =
          Boolean(partOutput) &&
          !result?.success &&
          (isPrelim ||
            result?.type === 'partial' ||
            result?.type === 'status' ||
            hasDraftChunk ||
            hasDraftDelta)

        if (isInterimOutput) {
          if (hasDraftChunk) {
            latestDraftContent = result.policyContent
            latestDraftContentMode = 'replace'
            isGeneratingPolicy = true
          } else if (hasDraftDelta) {
            latestDraftContent = result.policyContentDelta
            latestDraftContentMode = 'append'
            isGeneratingPolicy = true
          }
          continue
        }

        // Streaming in progress (input-streaming, input-available, etc.)
        // Don't set isGeneratingPolicy here — the tool may be paused by
        // needsApproval. Only set it when actual policyContent arrives.
        if (isActivePolicyToolState(partState)) {
          continue
        }

        // Legacy fallback: extract from tool input (old non-subagent path)
        const draft = (part as any).input?.policyContent
        if (typeof draft === 'string' && draft.trim().length > 0) {
          latestDraftContent = draft
          latestDraftContentMode = 'replace'
        }
        // Final result (non-preliminary)
        if (part.state === 'output-available' && result?.success) {
          generateCount += 1
          if (result?.success && typeof result?.policyDocumentId === 'string') {
            latestPolicyDocId = result.policyDocumentId
          }
        }
        continue
      }

      if (part.type === 'tool-updatePolicySection') {
        if (part.state === 'output-available' && part.output) {
          updateSectionCount += 1
          lastSectionUpdateSeq = seq++
          const result = parseToolPayload(part.output)
          if (result?.success && typeof result?.policyDocumentId === 'string') {
            latestPolicyDocId = result.policyDocumentId
          }
        }
        continue
      }

      if (part.type === 'tool-recordClaims') {
        if (part.state === 'output-available') {
          recordClaimsCount += 1
        }
        continue
      }

      if (part.type === 'tool-triageDraftClaim') {
        if (part.state === 'output-available') {
          triageCount += 1
        }
        continue
      }

      if (part.type === 'tool-checkPolicyCompliance') {
        if (part.state === 'output-available' && part.output) {
          checkerCount += 1
          lastCheckerSeq = seq++
          const result = parseToolPayload(part.output)
          const status = String(result?.status ?? '').trim()
          if (status) {
            const rawFindings = Array.isArray(result?.blockingFindings)
              ? result.blockingFindings
              : []
            latestCheckerSummary = {
              status,
              blockerCount: rawFindings.length,
              findings: rawFindings.map((f: any) => ({
                detail: String(f?.detail ?? ''),
                kind: f?.kind,
              })),
            }
          }
        }
        continue
      }

      if (part.type !== 'tool-invocation') continue
      const ti = part.toolInvocation
      if (!ti) continue

      if (
        ti.toolName === 'generatePolicy' &&
        (isActivePolicyToolState(ti.state) || ti.state === 'partial-result')
      ) {
        // Extract streaming policyContent from partial results
        if (ti.state === 'partial-result') {
          const partialResult = parseToolPayload(ti.result)
          if (
            typeof partialResult?.policyContent === 'string' &&
            partialResult.policyContent.trim().length > 0
          ) {
            latestDraftContent = partialResult.policyContent
            latestDraftContentMode = 'replace'
            isGeneratingPolicy = true
          } else if (
            typeof partialResult?.policyContentDelta === 'string' &&
            partialResult.policyContentDelta.length > 0
          ) {
            latestDraftContent = partialResult.policyContentDelta
            latestDraftContentMode = 'append'
            isGeneratingPolicy = true
          }
        }
      }

      if (ti.state !== 'result') continue

      const result = parseToolPayload(ti.result)
      if (ti.toolName === 'generatePolicy') {
        if (result?.requiresConfirmation) {
          requiresRegenerateConfirmation = true
        } else {
          generateCount += 1
          requiresRegenerateConfirmation = false
        }
        const draft = ti.args?.policyContent
        if (typeof draft === 'string' && draft.trim().length > 0) {
          latestDraftContent = draft
          latestDraftContentMode = 'replace'
        }
        if (result?.success && typeof result?.policyDocumentId === 'string') {
          latestPolicyDocId = result.policyDocumentId
        }
      } else if (ti.toolName === 'updatePolicySection') {
        updateSectionCount += 1
        lastSectionUpdateSeq = seq++
        if (result?.success && typeof result?.policyDocumentId === 'string') {
          latestPolicyDocId = result.policyDocumentId
        }
      } else if (
        ti.toolName === 'recordClaims'
      ) {
        recordClaimsCount += 1
      } else if (ti.toolName === 'checkPolicyCompliance') {
        checkerCount += 1
        lastCheckerSeq = seq++
        const status = String(result?.status ?? '').trim()
        if (status) {
          const rawFindings = Array.isArray(result?.blockingFindings)
            ? result.blockingFindings
            : []
          latestCheckerSummary = {
            status,
            blockerCount: rawFindings.length,
            findings: rawFindings.map((f: any) => ({
              detail: String(f?.detail ?? ''),
              kind: f?.kind,
            })),
          }
        }
      }
    }
  }

  const checkerStale =
    lastCheckerSeq >= 0 && lastSectionUpdateSeq > lastCheckerSeq

  return {
    generateCount,
    updateSectionCount,
    recordClaimsCount,
    checkerCount,
    triageCount,
    isGeneratingPolicy,
    latestPolicyDocId,
    latestDraftContent,
    latestDraftContentMode,
    latestCheckerSummary,
    checkerStale,
    latestReasoning,
    requiresRegenerateConfirmation,
  }
}

// ── Finding kind labels ──

/**
 * The short claim label shown on the in-document pill (`CLM-6W1J`) and
 * anywhere a commitment has to be matched back to it by eye.
 *
 * NOTE: the same expression is inlined in TriageClaimCard, DraftClaimCard,
 * DraftClaimEditorDialog, TestPropertiesPanel, PolicyGalaxyLineageDialog and
 * policy-claim-blocks. Those call sites should migrate here; consolidating
 * them is its own change.
 */
export function shortClaimId(claimId: string): string {
  return `CLM-${claimId.slice(-4).toUpperCase()}`
}

export function findingKindLabel(kind?: string): string {
  switch (kind) {
    case 'over_promise':
      return 'Wording may overstate current capabilities'
    case 'tsc_gap':
      return 'Missing control coverage'
    case 'cloud_conflict':
      return 'Conflicts with cloud scan results'
    case 'confirmation_required':
      return 'Needs your confirmation'
    default:
      return ''
  }
}
